import { useMemo, useState } from "react";
import { Sparkles, Search, X, Loader2, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { isToday, isYesterday, format } from "date-fns";

/**
 * The session list: search, day groupings, and a bounded default.
 *
 * It used to render every session in one flat column. That reached 29 rows in
 * a few days of use — 21 of them empty, because a session is created the
 * moment you press New session whether or not anything comes of it — each row
 * reading "Untitled session · Empty · 18 hours ago". Nothing distinguished one
 * from another and the whole column had to be scrolled past to reach anything.
 *
 * Three changes, in the order they matter:
 *   - only the ten most recent are shown, with the rest one click away
 *   - day headings, so "18 hours ago" twenty times becomes Today / Yesterday
 *     and a clock time per row
 *   - a search box once there are enough sessions for scanning to fail
 */

export interface SessionListItem {
  id: string;
  name: string;
  updated_at: string;
  /** Newest image in the session; null while loading or if it has none yet. */
  thumbnailUrl?: string | null;
  visualCount?: number;
  /**
   * Everything the session was asked for: every turn's prompt, the scene's
   * name, and its answers. The name is only the first prompt truncated, so
   * searching names alone misses every later turn — and for a scene session,
   * misses the whole request.
   */
  searchText?: string;
}

/**
 * One row in the session list: a small thumbnail, the name, and what is inside
 * the session. Shared by the sidebar and the empty state, which previously
 * styled their own rows and had drifted apart.
 *
 * The delete control appears on hover, and on keyboard focus too — a
 * hover-only affordance is unreachable otherwise.
 */
function SessionRow({
  session,
  active,
  onOpen,
  onDelete,
  busy,
  query,
}: {
  session: SessionListItem;
  active?: boolean;
  onOpen: () => void;
  onDelete: () => void;
  busy?: boolean;
  query?: string;
}) {
  const count = session.visualCount ?? 0;
  // When the hit is not in the name, show where it was. A row that matches
  // "cat" while reading "Untitled session" is otherwise unexplained.
  const snippet = useMemo(() => {
    const q = (query || "").toLowerCase();
    if (!q || session.name.toLowerCase().includes(q)) return null;
    const hay = session.searchText || "";
    const at = hay.toLowerCase().indexOf(q);
    if (at < 0) return null;
    const from = Math.max(0, at - 24);
    return (from > 0 ? "…" : "") + hay.slice(from, at + q.length + 32).replace(/\s+/g, " ").trim() + "…";
  }, [query, session.name, session.searchText]);
  return (
    <div
      className={cn(
        "group relative flex items-center transition-colors",
        active ? "bg-primary/10" : "hover:bg-muted/60",
      )}
    >
      {/* Small thumbnail rather than a card image — it identifies the session
          at a glance without turning the list into a gallery. */}
      <button onClick={onOpen} className="flex-1 min-w-0 flex items-center gap-2 px-2 py-1.5 text-left">
        <span className="w-7 h-7 shrink-0 rounded bg-muted overflow-hidden flex items-center justify-center">
          {session.thumbnailUrl ? (
            <img src={session.thumbnailUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
          ) : (
            <Sparkles className="w-3 h-3 text-muted-foreground/40" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block text-xs truncate",
              active ? "font-medium text-foreground" : "text-foreground/90",
            )}
          >
            {session.name}
          </span>
          <span className="block text-[10px] text-muted-foreground truncate">
            {count === 0 ? "Empty" : `${count} image${count === 1 ? "" : "s"}`}
            {" · "}
            {/* A clock time, because the day is already in the group heading.
                Twenty rows all reading "18 hours ago" told you nothing. */}
            {new Date(session.updated_at).toLocaleTimeString(undefined, {
              hour: "2-digit", minute: "2-digit",
            })}
          </span>
          {snippet && (
            <span className="block text-[10px] text-muted-foreground/80 truncate italic">
              {snippet}
            </span>
          )}
        </span>
      </button>
      <button
        onClick={onDelete}
        disabled={busy}
        aria-label={`Delete session ${session.name}`}
        title="Delete session"
        className={cn(
          "shrink-0 mr-1.5 w-6 h-6 rounded flex items-center justify-center transition-opacity",
          "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
          // focus, not just focus-visible, so tabbing to it always reveals it;
          // focus-within keeps it up while the row button holds focus.
          "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 disabled:opacity-40",
        )}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
      </button>
    </div>
  );
}

const DEFAULT_VISIBLE = 10;
/** Below this, scanning works and a search box is just another control. */
const SEARCH_THRESHOLD = 8;

function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (isToday(d)) return "Today";
  if (isYesterday(d)) return "Yesterday";
  return format(d, "d MMMM");
}

export function SessionList({
  sessions, activeId, deletingId, onOpen, onDelete, emptyMessage, loading,
}: {
  sessions: SessionListItem[];
  loading?: boolean;
  activeId?: string | null;
  deletingId?: string | null;
  onOpen: (id: string) => void;
  onDelete: (session: SessionListItem) => void;
  emptyMessage: string;
}) {
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);

  const matched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter(
      (s) => s.name.toLowerCase().includes(q) ||
        (s.searchText || "").toLowerCase().includes(q),
    );
  }, [sessions, query]);

  // A search is already a narrowing, so it shows everything it found rather
  // than hiding matches behind a second click.
  const limited = showAll || query.trim() ? matched : matched.slice(0, DEFAULT_VISIBLE);
  const hidden = matched.length - limited.length;

  const groups = useMemo(() => {
    const out: { label: string; items: SessionListItem[] }[] = [];
    for (const s of limited) {
      const label = dayLabel(s.updated_at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(s);
      else out.push({ label, items: [s] });
    }
    return out;
  }, [limited]);

  return (
    <div className="flex flex-col min-h-0 gap-2">
      {!loading && sessions.length >= SEARCH_THRESHOLD && (
        <div className="relative shrink-0">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search prompts and scenes"
            className="h-8 pl-7 pr-7 text-xs"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto rounded-md border border-border">
        {loading ? (
          <div className="p-2 space-y-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-9 w-full rounded" />)}
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2 py-3">{emptyMessage}</p>
        ) : matched.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2 py-3">
            Nothing matches "{query.trim()}".
          </p>
        ) : (
          groups.map((g) => (
            <div key={g.label}>
              {/* Sticky so the day stays visible while its rows scroll past. */}
              <p className="sticky top-0 z-10 bg-background/95 backdrop-blur px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/60">
                {g.label}
              </p>
              <div className="divide-y divide-border/60">
                {g.items.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    active={activeId === s.id}
                    busy={deletingId === s.id}
                    query={query.trim()}
                    onOpen={() => onOpen(s.id)}
                    onDelete={() => onDelete(s)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {hidden > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="shrink-0 text-xs text-muted-foreground hover:text-foreground py-1"
        >
          Show {hidden} older {hidden === 1 ? "session" : "sessions"}
        </button>
      )}
      {showAll && !query.trim() && matched.length > DEFAULT_VISIBLE && (
        <button
          onClick={() => setShowAll(false)}
          className="shrink-0 text-xs text-muted-foreground hover:text-foreground py-1"
        >
          Show fewer
        </button>
      )}
    </div>
  );
}
